import { useEffect, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  FolderPlus,
  RotateCcw,
  Save,
  Wand2,
  X,
} from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";
import { ApiError } from "../../api/client";
import {
  addReviewTags,
  decideReview,
  getReviewItem,
  type ReviewAction,
  type ReviewIssue,
  type ReviewItem,
  type Tagging,
} from "../../api/pqx";
import { updateQuestion } from "../../api/worker";
import { MathText } from "../../components/MathText";
import { letterAfter, letterBefore, sortedOptions, withOption } from "../../lib/options";
import { ImageUpload } from "../../components/ImageUpload";
import { PageImage } from "../../components/PageImage";
import { BAND_CLASSES, BAND_LABELS } from "./bookStates";
import { CorrectionReview } from "./CorrectionReview";

/** Plain names for the part of the record an issue points at. */
function fieldLabel(field: string | null): string {
  if (!field) return "Question";
  if (field === "stem") return "Question text";
  if (field.startsWith("options.")) return `Option ${field.slice(8)}`;
  if (field.startsWith("answer")) return "Answer";
  if (field.startsWith("figures") || field === "image") return "Figure";
  if (field === "explanation") return "Explanation";
  if (field === "topic" || field.startsWith("tags")) return "Topic";
  return field;
}

const EVIDENCE_LABELS: Record<string, string> = {
  page_reads: "The page reads",
  record_has: "Extracted as",
  solver_pick: "Checker's answer",
  adjudicator_pick: "Second checker's answer",
  book_key: "Book's answer",
  agreement: "Matches the page",
};

function evidenceText(key: string, value: unknown): string {
  if (key === "agreement" && typeof value === "number") return `${Math.round(value * 100)}%`;
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

/**
 * Our own issue kinds, named for a reviewer. The engine's checks (V1, K1…)
 * are shown by their code, which is how the engine's docs refer to them.
 */
const CHECK_NAMES: Record<string, string> = {
  "engine-final": "After the checks",
  "engine-rerun": "From the rerun",
  "verify-change": "Checker's fix",
  "verify-review": "Needs a person",
};
export const checkName = (check: string) => CHECK_NAMES[check] ?? check;

/** The editable parts of a question, as the reviewer changes them. */
interface Draft {
  question_text: string;
  options: Record<string, string>;
  correct_answer: string;
  explanation: string;
  /** The figure's link, or "" for none. */
  image_url: string;
}

function draftOf(item: ReviewItem): Draft {
  const q = item.question;
  return {
    question_text: q.question_text,
    // In letter order: an extracted question's options can arrive in any.
    options: sortedOptions(q.options ?? {}),
    correct_answer: q.correct_answer ?? "",
    explanation: q.explanation ?? "",
    image_url: q.image_url ?? "",
  };
}

const same = (a: Draft, b: Draft) => JSON.stringify(a) === JSON.stringify(b);

type Suggestion = NonNullable<ReviewIssue["suggestion"]>;

/**
 * The draft with the engine's fix written in. Null when the fix cannot apply
 * as it stands — options that are not an object of text, or a key that is not
 * one of the options — so the button never writes something that won't save.
 */
export function applySuggestion(draft: Draft, s: Suggestion): Draft | null {
  if (s.field === "stem") return s.value.trim() ? { ...draft, question_text: s.value } : null;
  if (s.field === "answer.key") {
    return s.value in draft.options ? { ...draft, correct_answer: s.value } : null;
  }
  try {
    const options = JSON.parse(s.value) as unknown;
    if (!options || typeof options !== "object" || Array.isArray(options)) return null;
    const entries = Object.entries(options as Record<string, unknown>);
    if (entries.length < 2 || entries.some(([, v]) => typeof v !== "string")) return null;
    const next = Object.fromEntries(entries) as Record<string, string>;
    // A key the new options no longer hold is cleared, not kept pointing nowhere.
    return {
      ...draft,
      options: next,
      correct_answer: draft.correct_answer in next ? draft.correct_answer : "",
    };
  } catch {
    return null;
  }
}

/** What applying the fix will do, in a reviewer's words. */
function suggestionLabel(s: Suggestion): string {
  if (s.field === "answer.key") return `Set the answer to ${s.value}`;
  if (s.field === "options") return "Use the corrected options";
  return "Use the complete question text";
}

/** Whether the draft already holds this fix, so the button can say so. */
function holds(draft: Draft, s: Suggestion): boolean {
  const applied = applySuggestion(draft, s);
  return applied !== null && same(applied, draft);
}

/**
 * Review a book one question at a time.
 *
 * Each question shows what the engine extracted beside every issue it raised,
 * so a reviewer can fix the record, mark an issue as not a problem, and
 * approve it — then move straight to the next one still under review.
 */
export function ReviewScreen() {
  const { bookId = "", questionId = "" } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState<ReviewItem | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let active = true;
    setItem(null);
    setError(null);
    setSaved(false);
    getReviewItem(bookId, questionId)
      .then((loaded) => {
        if (!active) return;
        setItem(loaded);
        setDraft(draftOf(loaded));
      })
      .catch((e) => active && setError(e instanceof Error ? e.message : "Couldn't load it."));
    return () => {
      active = false;
    };
  }, [bookId, questionId]);

  if (error && !item) return <p className="text-negative">{error}</p>;
  if (!item || !draft) return <p className="text-text-muted">Loading…</p>;
  // Bank questions the engine checked: accept or keep its correction.
  if (item.mode === "fix") {
    return <CorrectionReview bookId={bookId} item={item} onChange={setItem} />;
  }

  const q = item.question;
  const dirty = !same(draft, draftOf(item));
  const live = q.status === "active";
  const go = (id: string | null) => {
    if (id) navigate(`/books/${bookId}/review/${id}`);
  };

  const save = async (): Promise<boolean> => {
    if (!dirty) return true;
    const options = Object.fromEntries(
      Object.entries(draft.options).filter(([, text]) => text.trim() !== ""),
    );
    await updateQuestion(q.id, {
      questionText: draft.question_text.trim(),
      options,
      correctAnswer: draft.correct_answer || undefined,
      explanation: draft.explanation.trim() || undefined,
      // Only when it changed: an untouched figure is not re-sent.
      ...(draft.image_url !== (q.image_url ?? "") ? { imageUrl: draft.image_url || null } : {}),
    });
    return true;
  };

  const run = async (action: ReviewAction, issueId?: string) => {
    setBusy(true);
    setError(null);
    try {
      // Edits are saved first, so an approval covers what the reviewer sees.
      if (action === "approve" || action === "return") await save();
      const next = await decideReview(bookId, q.id, action, issueId);
      setItem(next);
      setDraft(draftOf(next));
      if (action === "approve" && next.next_pending_id && next.next_pending_id !== q.id) {
        go(next.next_pending_id);
      }
    } catch (e) {
      setError(e instanceof ApiError || e instanceof Error ? e.message : "That didn't work.");
    } finally {
      setBusy(false);
    }
  };

  /** Add the engine's topic or subtopic to the syllabus. Unsaved edits stay in the form. */
  const addTags = async (add: "topic" | "subtopic" | "both") => {
    setBusy(true);
    setError(null);
    try {
      const next = await addReviewTags(bookId, q.id, add);
      setItem(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't add it.");
    } finally {
      setBusy(false);
    }
  };

  const saveOnly = async () => {
    setBusy(true);
    setError(null);
    try {
      await save();
      const reloaded = await getReviewItem(bookId, q.id);
      setItem(reloaded);
      setDraft(draftOf(reloaded));
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't save.");
    } finally {
      setBusy(false);
    }
  };

  const open = q.issues.filter((i) => !i.dismissed);
  // A figure issue still open: the engine saw a figure it could not attach.
  const engine = q.engine_figures;
  const figureMissing =
    Boolean(engine?.uncropped) ||
    open.some((i) => i.field?.startsWith("figures") || i.field === "image" || i.check === "G3");
  /** Figures the engine cropped beyond the one a question can carry. */
  const extraFigures = Math.max(0, (engine?.cropped ?? 0) - 1);
  const dismissed = q.issues.filter((i) => i.dismissed);
  const done = item.remaining === 0;
  const field =
    "w-full rounded-lg border border-border bg-surface px-3 py-2 text-[14px] text-text outline-none focus:border-accent";

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link
          to={`/books/${bookId}`}
          className="inline-flex items-center gap-1 text-[12px] text-text-muted hover:text-text"
        >
          <ArrowLeft size={14} aria-hidden="true" /> Back to the book
        </Link>
        <p className="text-[12px] text-text-muted tabular-nums">
          Question {item.position} of {item.total} ·{" "}
          {done ? "every question reviewed" : `${item.remaining} still to review`}
        </p>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <h1 className="text-[18px] font-bold lg:text-[22px]">
          {q.year ? `${q.year} · ` : ""}Question {q.number}
        </h1>
        {q.band ? (
          <span className={`rounded px-2 py-0.5 text-[12px] font-semibold ${BAND_CLASSES[q.band]}`}>
            {BAND_LABELS[q.band]}
          </span>
        ) : null}
        {live ? (
          <span className="inline-flex items-center gap-1 rounded bg-positive/15 px-2 py-0.5 text-[12px] font-semibold text-positive">
            <CheckCircle2 size={12} aria-hidden="true" /> Approved, live
          </span>
        ) : (
          <span className="rounded bg-surface-high px-2 py-0.5 text-[12px] text-text-muted">
            Under review
          </span>
        )}
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
        {/* The record, editable */}
        <section className="flex flex-col gap-4 rounded-xl border border-border bg-surface p-5">
          {/* The figure: upload one the engine could not crop, or replace a
              wrong crop. Saved with the question's other changes. */}
          {figureMissing && !draft.image_url ? (
            <p className="rounded-lg bg-warning/10 px-3 py-2 text-[12px] text-warning">
              The engine says this question needs a figure it couldn't attach. Upload it from the
              book.
            </p>
          ) : null}
          {extraFigures > 0 ? (
            <p className="rounded-lg bg-warning/10 px-3 py-2 text-[12px] text-warning">
              The engine found {extraFigures + 1} figures for this question; only the first was
              kept. If the others matter (for example, one per option), combine them into one image
              and upload it here.
            </p>
          ) : null}
          {draft.image_url && engine && engine.cropped > 0 && draft.image_url === q.image_url ? (
            <p className="text-[12px] text-text-muted">
              Cropped from the book by the engine. Check it shows the whole figure, and replace it
              if not.
            </p>
          ) : null}
          <ImageUpload
            label={`Figure for ${q.year} question ${q.number}`}
            value={draft.image_url}
            disabled={live}
            onChange={(url) => setDraft({ ...draft, image_url: url })}
          />

          <label className="block">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-text-muted">
              Question text
            </span>
            <textarea
              value={draft.question_text}
              disabled={live}
              onChange={(e) => setDraft({ ...draft, question_text: e.target.value })}
              rows={4}
              className={field}
            />
            <MathText text={draft.question_text} className="mt-2 block text-[14px] leading-[1.5]" />
          </label>

          <fieldset>
            <legend className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
              Options · tick the correct one
            </legend>
            {/* The engine can drop an option it couldn't read: a question may
                start at B, or stop before E. Add above only when there's room. */}
            {!live && letterBefore(draft.options) && Object.keys(draft.options).length ? (
              <button
                type="button"
                onClick={() =>
                  setDraft({
                    ...draft,
                    options: withOption(draft.options, letterBefore(draft.options)!),
                  })
                }
                className="mb-2 text-[12px] font-semibold text-accent hover:underline"
              >
                + Add option {letterBefore(draft.options)} above
              </button>
            ) : null}
            <div className="flex flex-col gap-2">
              {Object.entries(draft.options).map(([key, text]) => (
                <div key={key} className="flex items-start gap-2">
                  <input
                    type="radio"
                    name="correct"
                    aria-label={`Option ${key} is correct`}
                    checked={draft.correct_answer === key}
                    disabled={live}
                    onChange={() => setDraft({ ...draft, correct_answer: key })}
                    className="mt-3"
                  />
                  <span className="mt-2 w-5 shrink-0 text-[13px] font-bold">{key}</span>
                  <div className="min-w-0 flex-1">
                    <input
                      aria-label={`Option ${key}`}
                      value={text}
                      disabled={live}
                      onChange={(e) =>
                        setDraft({ ...draft, options: { ...draft.options, [key]: e.target.value } })
                      }
                      className={field}
                    />
                    {text.includes("$") || text.includes("\\(") ? (
                      <MathText text={text} className="mt-1 block text-[13px]" />
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
            {!live && letterAfter(draft.options) ? (
              <button
                type="button"
                onClick={() =>
                  setDraft({
                    ...draft,
                    options: withOption(draft.options, letterAfter(draft.options)!),
                  })
                }
                className="mt-2 text-[12px] font-semibold text-accent hover:underline"
              >
                + Add option {letterAfter(draft.options)}
              </button>
            ) : null}
            {!draft.correct_answer ? (
              <p className="mt-2 text-[12px] text-warning">No correct answer chosen yet.</p>
            ) : null}
          </fieldset>

          <label className="block">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-text-muted">
              Explanation (optional)
            </span>
            <textarea
              value={draft.explanation}
              disabled={live}
              onChange={(e) => setDraft({ ...draft, explanation: e.target.value })}
              rows={3}
              className={field}
            />
          </label>

          <TopicPanel
            topic={q.topic}
            subtopic={q.subtopic}
            tagging={item.tagging ?? null}
            busy={busy || live}
            onAdd={(add) => void addTags(add)}
          />
          <p className="text-[12px] text-text-muted">
            <Link to={`/questions/${q.id}`} className="text-accent-light">
              Open in the full editor
            </Link>{" "}
            to pick a different topic, or change the figure or type.
          </p>
        </section>

        {/* The engine's issues */}
        <aside className="flex flex-col gap-3">
          <h2 className="text-[14px] font-semibold">
            Issues to check{" "}
            <span className="font-normal text-text-muted">
              ({open.length} open{dismissed.length ? `, ${dismissed.length} dismissed` : ""})
            </span>
          </h2>
          {q.checking ? (
            <p role="status" className="rounded-lg bg-info/10 px-4 py-3 text-[13px] text-info">
              Checking… The engine is still checking this book's answers. You can edit this question
              now; its issues appear and approving opens when the checks finish. Anything you change
              is kept, and if the engine disagrees, it says so here.
            </p>
          ) : q.issues.length === 0 ? (
            <p className="rounded-lg bg-positive/10 px-4 py-3 text-[13px] text-positive">
              The engine raised nothing on this question. Check it reads right, then approve.
            </p>
          ) : null}
          {q.rerun_live && live ? (
            <p role="status" className="rounded-lg bg-info/10 px-4 py-3 text-[13px] text-info">
              This book was rerun while this question was live, so its new values wait below instead
              of changing what students see. To use one, choose <strong>Send back to review</strong>
              , apply it, then approve the question again.
            </p>
          ) : null}
          {[...open, ...dismissed].map((issue) => (
            <IssueCard
              key={issue.id}
              issue={issue}
              busy={busy || live}
              draft={draft}
              onApply={(next) => setDraft(next)}
              onToggle={() => void run(issue.dismissed ? "restore" : "dismiss", issue.id)}
            />
          ))}
          {q.page_image_url ? (
            <section className="mt-2 flex flex-col gap-2">
              <h2 className="text-[14px] font-semibold">The printed question</h2>
              <p className="text-[12px] text-text-muted">
                The question as printed in the book. Compare it with the issues above.
              </p>
              <PageImage
                src={q.page_image_url}
                label={`${q.year} question ${q.number} as printed`}
              />
            </section>
          ) : null}
        </aside>
      </div>

      {error ? (
        <p role="alert" className="mt-4 text-[13px] text-negative">
          {error}
        </p>
      ) : null}
      {saved && !dirty ? <p className="mt-4 text-[13px] text-positive">Saved.</p> : null}

      {/* Actions */}
      <div className="sticky bottom-0 mt-5 flex flex-wrap items-center gap-2 border-t border-border bg-bg py-3">
        <button
          type="button"
          disabled={!item.previous_id || busy}
          onClick={() => go(item.previous_id)}
          className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-2 text-[13px] disabled:opacity-40"
        >
          <ArrowLeft size={14} aria-hidden="true" /> Previous
        </button>
        <button
          type="button"
          disabled={!item.next_id || busy}
          onClick={() => go(item.next_id)}
          className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-2 text-[13px] disabled:opacity-40"
        >
          Next <ArrowRight size={14} aria-hidden="true" />
        </button>
        <span className="flex-1" />
        {live ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => void run("return")}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-4 py-2 text-[13px] font-semibold disabled:opacity-50"
          >
            <RotateCcw size={14} aria-hidden="true" /> Send back to review
          </button>
        ) : (
          <>
            <button
              type="button"
              disabled={!dirty || busy}
              onClick={() => void saveOnly()}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border px-4 py-2 text-[13px] font-semibold disabled:opacity-40"
            >
              <Save size={14} aria-hidden="true" /> Save changes
            </button>
            <button
              type="button"
              // The engine's checks may still change this question's answer:
              // approving opens once they finish.
              disabled={busy || q.checking === true}
              title={
                q.checking
                  ? "Approving opens when the engine finishes checking this book"
                  : undefined
              }
              onClick={() => void run("approve")}
              className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-4 py-2 text-[13px] font-semibold text-bg disabled:opacity-50"
            >
              <Check size={14} aria-hidden="true" />
              {dirty ? "Save and approve" : "Approve"}
              {open.length ? ` (${open.length} open)` : ""}
            </button>
          </>
        )}
      </div>
      {done && live ? (
        <p className="mt-2 text-[13px] text-positive">
          That was the last one. Every question in this book has been reviewed.
        </p>
      ) : null}
    </div>
  );
}

function IssueCard({
  issue,
  busy,
  draft,
  onApply,
  onToggle,
}: {
  issue: ReviewIssue;
  busy: boolean;
  draft: Draft;
  onApply: (next: Draft) => void;
  onToggle: () => void;
}) {
  const evidence = Object.entries(issue.evidence).filter(
    ([, v]) => v !== null && v !== undefined && v !== "",
  );
  const suggestion = issue.dismissed ? null : (issue.suggestion ?? null);
  const applied = suggestion ? applySuggestion(draft, suggestion) : null;
  return (
    <div
      className={`rounded-lg border p-3 text-[13px] ${
        issue.dismissed ? "border-border opacity-60" : "border-warning/40 bg-warning/5"
      }`}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle
          size={14}
          className={`mt-0.5 shrink-0 ${issue.dismissed ? "text-text-muted" : "text-warning"}`}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
            {fieldLabel(issue.field)} · {checkName(issue.check)}
          </p>
          <p className={`mt-1 ${issue.dismissed ? "line-through" : ""}`}>{issue.message}</p>
          {evidence.length ? (
            <dl className="mt-2 flex flex-col gap-1 text-[12px]">
              {evidence.map(([key, value]) => (
                <div key={key}>
                  <dt className="text-text-muted">{EVIDENCE_LABELS[key] ?? key}</dt>
                  <dd className="break-words">
                    <MathText text={evidenceText(key, value)} />
                  </dd>
                </div>
              ))}
            </dl>
          ) : null}
          {suggestion && applied ? (
            <div className="mt-2 rounded-md border border-accent/30 bg-accent/5 p-2 text-[12px]">
              <p className="text-text-muted">
                Suggested fix{suggestion.by ? ` (from the ${suggestion.by})` : ""}
              </p>
              {suggestion.field !== "answer.key" ? (
                <div className="mt-1 break-words">
                  <MathText
                    text={
                      suggestion.field === "options"
                        ? Object.entries(applied.options)
                            .map(([k, v]) => `${k}. ${v}`)
                            .join("\n")
                        : suggestion.value
                    }
                  />
                </div>
              ) : null}
              {holds(draft, suggestion) ? (
                <p className="mt-1 inline-flex items-center gap-1 font-semibold text-positive">
                  <Check size={12} aria-hidden="true" /> Applied — save or approve to keep it
                </p>
              ) : (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onApply(applied)}
                  className="mt-1 inline-flex items-center gap-1 font-semibold text-accent-light disabled:opacity-40"
                >
                  <Wand2 size={12} aria-hidden="true" /> {suggestionLabel(suggestion)}
                </button>
              )}
            </div>
          ) : null}
        </div>
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={onToggle}
        className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold text-accent-light disabled:opacity-40"
      >
        {issue.dismissed ? (
          <>
            <RotateCcw size={12} aria-hidden="true" /> Bring back
          </>
        ) : (
          <>
            <X size={12} aria-hidden="true" /> Not a problem
          </>
        )}
      </button>
    </div>
  );
}

/**
 * Where the question is filed, and the engine's topic if the syllabus lacks it.
 *
 * The engine names a topic and subtopic for every question. When the bank has
 * no topic of that name, the question imports with none; here the reviewer can
 * add the engine's name to the syllabus in one click — after which every later
 * question and book with that tag files under it automatically.
 */
function TopicPanel({
  topic,
  subtopic,
  tagging,
  busy,
  onAdd,
}: {
  topic: string | null;
  subtopic: string | null;
  tagging: Tagging | null;
  busy: boolean;
  onAdd: (add: "topic" | "subtopic" | "both") => void;
}) {
  const newTopic = tagging?.topic && !tagging.topic.exists ? tagging.topic.name : null;
  const newSub = tagging?.subtopic && !tagging.subtopic.exists ? tagging.subtopic.name : null;
  // A subtopic needs a topic to sit under: a new topic, the engine's existing
  // one, or the one the question is already filed under.
  const subParent = newTopic ?? tagging?.subtopic?.under ?? null;
  // Already filed exactly where the engine suggests: nothing to offer.
  const offers = Boolean(
    (newTopic && topic !== newTopic) || (newSub && subtopic !== newSub && subParent),
  );
  const button =
    "inline-flex items-center gap-1 rounded-md border border-accent/40 px-2 py-1 text-[12px] font-semibold text-accent-light disabled:opacity-40";

  return (
    <div className="rounded-lg border border-border px-3 py-2 text-[13px]">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">Topic</p>
      <p className="mt-0.5">
        {topic ?? <span className="text-warning">None yet</span>}
        {subtopic ? <span className="text-text-muted"> · {subtopic}</span> : null}
      </p>
      {offers ? (
        <div className="mt-2 rounded-md bg-accent/5 p-2 text-[12px]">
          <p className="text-text-muted">
            The engine tagged this{" "}
            <b className="text-text">{newTopic ?? tagging?.topic?.name ?? topic}</b>
            {newSub ? (
              <>
                {" "}
                · <b className="text-text">{newSub}</b>
              </>
            ) : null}
            , which {newTopic && newSub ? "aren't" : "isn't"} in this subject's topics yet.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {newTopic && newSub ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => onAdd("both")}
                className={button}
              >
                <FolderPlus size={12} aria-hidden="true" /> Add both and use them
              </button>
            ) : null}
            {newTopic ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => onAdd("topic")}
                className={button}
              >
                <FolderPlus size={12} aria-hidden="true" /> Add topic “{newTopic}”
              </button>
            ) : null}
            {newSub && !newTopic && subParent ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => onAdd("subtopic")}
                className={button}
              >
                <FolderPlus size={12} aria-hidden="true" /> Add subtopic “{newSub}” under{" "}
                {subParent}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
